import { useState, useCallback, useMemo } from 'react';
import { WorkflowFilters } from '../../types/workflow';

/**
 * Return type for useWorkflowFilters hook
 */
export interface UseWorkflowFiltersReturn {
  filters: WorkflowFilters;
  setFilter: <K extends keyof WorkflowFilters>(key: K, value: WorkflowFilters[K]) => void;
  setFilters: (filters: Partial<WorkflowFilters>) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

/**
 * Default filter values for workflow list
 */
export const DEFAULT_FILTERS: WorkflowFilters = {
  search: '',
  statusCode: '',
  priority: '',
  overdueOnly: false,
  showCompleted: false,
  sampleType: '',
  datePreset: '',
  dateFrom: null,
  dateTo: null,
  page: 1,
  pageSize: 20,
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

/**
 * Keys that should not trigger page reset when changed
 */
const PAGINATION_KEYS: (keyof WorkflowFilters)[] = ['page', 'pageSize'];

/**
 * Custom hook for managing workflow filter state
 *
 * Single Responsibility: ONLY manages filter state
 * - No data fetching
 * - No side effects
 * - Pure state management
 *
 * @returns Filter state and mutation functions
 *
 * @example
 * ```tsx
 * const { filters, setFilter, resetFilters, hasActiveFilters } = useWorkflowFilters();
 *
 * // Set a single filter (automatically resets page to 1)
 * setFilter('search', 'tomato');
 *
 * // Set multiple filters at once
 * setFilters({ statusCode: 'AWAITING_REVIEW', priority: 'HIGH' });
 *
 * // Reset all filters to defaults
 * resetFilters();
 * ```
 */
export function useWorkflowFilters(): UseWorkflowFiltersReturn {
  const [filters, setFiltersState] = useState<WorkflowFilters>(DEFAULT_FILTERS);

  /**
   * Set a single filter value
   * Automatically resets to page 1 when non-pagination filters change
   */
  const setFilter = useCallback(<K extends keyof WorkflowFilters>(
    key: K,
    value: WorkflowFilters[K]
  ) => {
    setFiltersState(prev => {
      // Determine if this is a pagination-related key
      const isPaginationKey = PAGINATION_KEYS.includes(key);

      // When setting page directly, respect the value
      // When setting pageSize, reset to page 1 to avoid out-of-bounds
      // For all other filters, reset to page 1
      let newPage = prev.page;
      if (key === 'page') {
        newPage = value as number;
      } else if (!isPaginationKey || key === 'pageSize') {
        newPage = 1;
      }

      return {
        ...prev,
        [key]: value,
        page: newPage,
      };
    });
  }, []);

  /**
   * Set multiple filters at once
   * Always resets to page 1 for batch updates (unless page is explicitly provided)
   */
  const setFilters = useCallback((newFilters: Partial<WorkflowFilters>) => {
    setFiltersState(prev => ({
      ...prev,
      ...newFilters,
      // Reset to page 1 unless page is explicitly provided in newFilters
      page: newFilters.page ?? 1,
    }));
  }, []);

  /**
   * Reset all filters to default values
   */
  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  /**
   * Indicates whether any non-default filters are active
   * Excludes pagination and sorting from consideration
   */
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.statusCode !== '' ||
      filters.priority !== '' ||
      filters.overdueOnly ||
      filters.showCompleted ||
      filters.sampleType !== '' ||
      filters.datePreset !== ''
    );
  }, [
    filters.search,
    filters.statusCode,
    filters.priority,
    filters.overdueOnly,
    filters.showCompleted,
    filters.sampleType,
    filters.datePreset,
  ]);

  /**
   * Count of active non-default filters
   * Useful for showing filter badge counts
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.statusCode) count++;
    if (filters.priority) count++;
    if (filters.overdueOnly) count++;
    if (filters.showCompleted) count++;
    if (filters.sampleType) count++;
    if (filters.datePreset) count++;
    return count;
  }, [
    filters.search,
    filters.statusCode,
    filters.priority,
    filters.overdueOnly,
    filters.showCompleted,
    filters.sampleType,
    filters.datePreset,
  ]);

  return {
    filters,
    setFilter,
    setFilters,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}

// Re-export WorkflowFilters from centralized types for convenience
export type { WorkflowFilters };
